/** 日志（屏规格 `docs/design/screens/logs.md`，#4598）。
 *
 * - 事件流：`GET /events`（SSE）实时 + 断线由 `EventSource` 带 `Last-Event-ID` 补播；seq 不连续插缺口行；
 * - 历史：`GET /events/history`（读事件日志，跨重启，保留 90 天），按 `before_seq` 往前翻；
 * - 级别 `level` 与来源 `source` 由后端随事件给出（纸上盘进程的事件汇入后 `source=paper`），前端不猜；
 * - 告警 `GET /alerts`、纸上盘控制事件 `GET /paper/events` 单列。
 */

import { useMemo, useState } from "react";

import { Card, Lede, Note, PageHead } from "../../components/Card";
import { Table } from "../../components/Table";
import { ts } from "../../lib/format";
import { filterEvents, mergeEvents, type EventLevel, type EventRow } from "../../lib/g1";
import { readEventsHistory } from "../../lib/g1";
import { useAlertsLoose, useControlEvents, useEventStream } from "../../lib/g1Queries";
import { EventTable, Load } from "../overview/parts";

const LEVELS: (EventLevel | "")[] = ["", "INFO", "WARN", "ERROR"];
const SOURCES = ["", "inbox", "risk", "strategy", "ledger", "recon", "runner", "paper", "alerts"];

export function LogsScreen(): React.ReactNode {
  const stream = useEventStream(5000);
  const alerts = useAlertsLoose();
  const control = useControlEvents();
  const [level, setLevel] = useState<EventLevel | "">("");
  const [source, setSource] = useState("");
  const [older, setOlder] = useState<EventRow[]>([]);
  const [cursor, setCursor] = useState<number | null | undefined>(undefined);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const rows = useMemo(() => mergeEvents(stream.rows, older.flatMap((r) => (r.event ? [r.event] : [])), 10_000), [stream.rows, older]);
  const shown = filterEvents(rows, level, source);
  const hiddenHalt = (level || source) && rows.some((r) => r.event?.type === "halt") && !shown.some((r) => r.event?.type === "halt");
  const counts = rows.reduce<Record<string, number>>((acc, row) => {
    if (row.event) acc[row.event.level] = (acc[row.event.level] ?? 0) + 1;
    return acc;
  }, {});

  async function loadOlder(): Promise<void> {
    setLoading(true);
    setHistoryError(null);
    try {
      const oldest = rows.filter((r) => r.kind === "event").at(-1)?.seq ?? null;
      const before = cursor ?? oldest;
      const page = await readEventsHistory({ before_seq: before ?? undefined, limit: 500 });
      setOlder((current) => [...current, ...page.events.map((event) => ({ kind: "event" as const, seq: event.seq, event }))]);
      setCursor(page.next_before_seq);
    } catch (cause) {
      setHistoryError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <PageHead title="日志" sub="GET /events（SSE）· GET /events/history" />
      <Lede>内核结构化事件的时间线，最新在上；级别与来源由后端给出。纸上盘进程的停机 / 放行 / 桥接与每次跑一天的结果汇入同一事件流（来源 paper）。</Lede>
      {stream.state === "reconnecting" ? (
        <Note tone="warn">事件流已断开，正在重连（将从 #{stream.lastSeq ?? 0} 补播）</Note>
      ) : null}
      {stream.error ? <Note tone="bad">事件形状不符：{stream.error}</Note> : null}
      <Card
        title="事件流"
        sub={`${stream.state === "open" ? "已连接" : stream.state === "connecting" ? "连接中" : "重连中"} · 已加载 ${rows.filter((r) => r.kind === "event").length} 条（INFO ${counts.INFO ?? 0} / WARN ${counts.WARN ?? 0} / ERROR ${counts.ERROR ?? 0}，只对已加载范围计数）`}
        actions={
          <span className="fieldrow" style={{ display: "inline-flex", gap: 6 }}>
            <select value={level} onChange={(e) => setLevel(e.target.value as EventLevel | "")} aria-label="级别">
              {LEVELS.map((v) => (
                <option key={v} value={v}>
                  {v || "全部级别"}
                </option>
              ))}
            </select>
            <select value={source} onChange={(e) => setSource(e.target.value)} aria-label="来源">
              {SOURCES.map((v) => (
                <option key={v} value={v}>
                  {v || "全部来源"}
                </option>
              ))}
            </select>
            {level || source ? (
              <button type="button" className="btn sm" onClick={() => { setLevel(""); setSource(""); }}>
                清除筛选
              </button>
            ) : null}
          </span>
        }
        nopad
      >
        {hiddenHalt ? <Note tone="bad">有停机事件被当前筛选隐藏——停机提示不随筛选消失，请清除筛选查看。</Note> : null}
        <EventTable
          rows={shown}
          empty={level || source ? "当前筛选下没有事件" : "暂无事件（服务启动后尚未发生账务动作）"}
        />
        <div style={{ padding: 8 }}>
          <button type="button" className="btn sm" disabled={loading || cursor === null} onClick={() => void loadOlder()}>
            {cursor === null ? "历史已到头（保留 90 天）" : loading ? "读取中…" : "加载更早的历史"}
          </button>
          {historyError ? <Note tone="bad">历史查询失败：{historyError}；当前只显示本次连接以来与内存缓冲中的事件</Note> : null}
        </div>
      </Card>
      <div className="grid g2" style={{ marginTop: 12 }}>
        <Card title="告警" sub="GET /alerts · 最近 7 天" nopad>
          <Load q={alerts} what="告警">
            {(list) => (
              <Table
                cols={[{ label: "时间", width: "26%" }, { label: "类别", width: "24%" }, { label: "摘要", wrap: true }]}
                rows={[...list].reverse().map((a, i) => ({ key: `${a.ts_utc}-${i}`, cls: "warn", cells: [a.ts_utc.slice(0, 19).replace("T", " "), a.kind, `${a.summary}${a.detail ? ` · ${a.detail.slice(0, 200)}` : ""}`] }))}
                empty="最近 7 天无告警"
              />
            )}
          </Load>
        </Card>
        <Card title="纸上盘控制事件" sub="GET /paper/events" nopad>
          <Load q={control} what="纸上盘控制事件">
            {(list) => (
              <Table
                cols={[{ label: "时间", width: "26%" }, { label: "类型", width: "12%" }, { label: "策略", width: "24%" }, { label: "操作员 / 理由", wrap: true }]}
                rows={list.map((row, i) => ({ key: `${row.ts_ns}-${i}`, cls: row.kind === "halt" ? "bad" : "", cells: [ts(row.ts_ns), row.kind, row.strategy_id ?? "—", `${row.operator ?? "—"}：${row.reason ?? "—"}`] }))}
                empty="没有停机 / 放行事件"
              />
            )}
          </Load>
        </Card>
      </div>
    </>
  );
}
